import { useState, type ReactNode } from "react"
import { Check, Eye, Trash2 } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { DocPreviewDialog } from "@/features/transit/components/doc-preview-dialog"
import { cn } from "@/lib/utils"

interface UploadedDocRowProps {
  otId: number | string
  // The artifact to render. `name` shows on the top line, the date /
  // uploader / extras append onto the metadata line. `storedKey` is what
  // we hand to the preview dialog and presigned-URL endpoint.
  doc: {
    name: string
    storedKey: string
    docType?: string | null
    uploadedAt?: string | null
    uploadedBy?: string | null
    note?: string | null
  }
  // Optional title to render above the filename — used by CCC doc rows
  // where the slot context isn't part of the artifact name. Defaults to
  // `doc.name` rendered as the filename only.
  title?: ReactNode
  // Optional inline pill (e.g. amount, reference) shown after the
  // filename on the metadata line.
  trailingMeta?: ReactNode
  // Optional inline badge (e.g. "Photo", "Levy") rendered next to the title.
  badge?: ReactNode
  onDelete: () => void
  isDeleting?: boolean
  className?: string
  // When the OT is locked (archived), hide the delete affordance and
  // keep the row read-only with preview only.
  locked?: boolean
}

// Shared list-item card used across panels (photos, redevances,
// empotage files, CCC docs, etc.). Visual signature matches the
// documents-modal cards: neutral card, dark green tick, Eye + Trash
// actions, click filename or eye icon → inline preview.
export function UploadedDocRow({
  otId,
  doc,
  title,
  trailingMeta,
  badge,
  onDelete,
  isDeleting,
  className,
  locked,
}: UploadedDocRowProps) {
  const { t } = useTranslation("transit")
  const [previewOpen, setPreviewOpen] = useState(false)

  return (
    <>
      <div
        className={cn(
          "group bg-card hover:bg-muted/30 flex items-center gap-3 rounded-md border px-3 py-2 transition-colors",
          className,
        )}
      >
        <span className="bg-emerald-600 text-white flex size-4 flex-shrink-0 items-center justify-center rounded-full">
          <Check className="size-2.5" />
        </span>

        <div className="min-w-0 flex-1">
          {title ? (
            <div className="flex items-center gap-1.5">
              <span className="text-foreground truncate text-sm font-medium">
                {title}
              </span>
              {badge}
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-foreground truncate text-sm font-medium">
                {doc.name}
              </span>
              {badge}
            </div>
          )}
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            className="text-muted-foreground hover:text-foreground mt-0.5 flex w-full items-center gap-1 text-left text-[11px] tabular-nums hover:underline"
          >
            {title ? (
              <span className="truncate font-mono">{doc.name}</span>
            ) : null}
            <span className="text-muted-foreground/70 flex-shrink-0">
              {title ? "· " : ""}
              {doc.uploadedAt?.slice(0, 10) ?? "—"}
              {doc.uploadedBy ? ` · ${doc.uploadedBy}` : ""}
              {trailingMeta ? <> · {trailingMeta}</> : null}
            </span>
          </button>
          {doc.note ? (
            <p className="text-muted-foreground/80 mt-0.5 line-clamp-2 text-[11px]">
              {doc.note}
            </p>
          ) : null}
        </div>

        <div className="flex flex-shrink-0 items-center gap-0.5">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={() => setPreviewOpen(true)}
                aria-label={t("preview.btn")}
              >
                <Eye className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("preview.btn")}</TooltipContent>
          </Tooltip>
          {locked ? null : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 hover:text-red-600"
                  onClick={onDelete}
                  disabled={isDeleting}
                  aria-label={t("docCard.delete")}
                >
                  <Trash2 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("docCard.replaceHint")}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <DocPreviewDialog
        otId={otId}
        doc={previewOpen ? doc : null}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  )
}
